import React, { useState, useEffect, useContext } from "react";
import { Container, Button, Spinner } from "reactstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { BreadcrumbContext } from "Layouts/Header";
import styles from "../../../../modules/Library/AdverseEvent/css/adverseEvent.module.scss";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "../../../../common/constants/PermissionConstants";
import SubjectPrintModel from "../components/RePrintModal";
import CommonButton from "Components/Common/CommonButton";
import { showToastMessage } from "common/service/CommonService";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useGlobalStudy } from "../../../../context/StudyContext";
import { useAuditService } from "modules/Audit/api/useAuditQuery"; 
import LabelTypeTabs, { LabelTab } from "modules/PrintLabel/MainPrintListTabs/helper/printlist.constant";
import { reprintColumn } from "../helper/reprint.columns";
import { useRePrintList } from "../api/useReprint";
import { usePermission } from "context/PermissionContext";
import { PRINT_LABEL_PATHS } from "Routes/paths";
import NoStudySelectedPlaceholder from "../../../../Components/Common/NoStudySelectedPlaceholder";

/* ------------------------------------------------------------------ */
/* Interfaces                                                            */
/* ------------------------------------------------------------------ */

interface PrintRecord {
  uniqueKey?:string;
  printid: string;
  printdate: string;
  printtime: string;
  subjectno: string;
  studycode: string;
  studyid: number;
  volunteerassignmentid: number;
  qty: number;
  printcount?: number;
  totalcount: number;
  printby?: string; // user display name from tbl_user join
  barcode?: string; // barcode number
  printstatus?: string; // live print status from API
  isreprint?: boolean; // whether this is a reprint
  labeltype?: string;
  labelicon?: string;
  period_number?: string;
  timepoint_number?: string;
  aliquot_number?: string;
}

/* ------------------------------------------------------------------ */
/* Main Component                                                        */
/* ------------------------------------------------------------------ */

const RePrintLabelList: React.FC = () => {
  document.title = "Print History & Reprint | ZENTIXS VISTA";

  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumbContext = useContext(BreadcrumbContext);
  const { selectedStudy } = useGlobalStudy();

  /* ── State ─────────────────────────────────────────────────────── */
  const [activeTab, setActiveTab] = useState<LabelTab>("subject");

  const printLabelPathByTab: Record<LabelTab, string> = {
    subject: PRINT_LABEL_PATHS.TABS,
    idcard: `${PRINT_LABEL_PATHS.TABS}/icard`,
    vacutainer: `${PRINT_LABEL_PATHS.TABS}/vacutainer`,
    cryovial: `${PRINT_LABEL_PATHS.TABS}/cryovial`,
    box: `${PRINT_LABEL_PATHS.TABS}/box`,
    dispensing: `${PRINT_LABEL_PATHS.TABS}/dispensing`,
  };

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Print Labels", link: printLabelPathByTab[activeTab] },
      { text: "Print History & Reprint" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [activeTab]);

  /* ── Initial Tab from Navigation State ─────────────────────────── */
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab as LabelTab);
    }
  }, [location.state]);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [reprintModalOpen, setReprintModalOpen] = useState(false);
  const [reprintRecord, setReprintRecord] = useState<PrintRecord | null>(null);

  const [isManualRefetch, setIsManualRefetch] = useState(false);

  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  /* ── API Data Fetching ────────────────────────────────────────── */
  const tabLabelMap: Record<LabelTab, string> = {
    subject: "Subject Label",
    vacutainer: "Vacutainer Label",
    cryovial: "Riavial Label",
    box: "Dispatch Box Label",
    dispensing: "Dispensing Label",
    idcard: "IdCard Label",
  };

  const { SelectAllQuery } = useRePrintList({
    printtype: tabLabelMap[activeTab],
    searchtext: searchText,
    currentpage: currentPage,
    pagesize: pageSize,
    studyid: selectedStudy?.studyid || null,
  });

  /* Reset manual flag once the fetch settles */
  useEffect(() => {
    if (!SelectAllQuery.isFetching) {
      setIsManualRefetch(false);
    }
  }, [SelectAllQuery.isFetching]);

  const apiResponse = SelectAllQuery.data;
  const rawData: any[] = Array.isArray(apiResponse?.data)
    ? apiResponse.data
    : [];
  const totalRecords =
    rawData.length > 0
      ? rawData[0].totalcount || rawData[0].total_count || 0
      : 0;

  const tabConfig: Record<LabelTab, { label: string; icon: string }> = {
    subject: { label: "Subject Label", icon: "ri-user-line" },
    vacutainer: { label: "Vacutainer Label", icon: "ri-flask-line" },
    cryovial: { label: "Cryovial Label", icon: "ri-test-tube-line" },
    box: { label: "Box Label", icon: "ri-archive-drawer-line" },
    dispensing: { label: "Dispensing Label", icon: "ri-price-tag-3-line" },
    idcard: { label: "IdCard Label", icon: "ri-id-card-line" },
  };

  const apiData: PrintRecord[] = rawData.map((item: any, idx: number) => ({
    ...item,
    uniqueKey: `${item.printid}_${idx}`,
    labeltype: tabConfig[activeTab].label,
    labelicon: tabConfig[activeTab].icon,
  }));

  /* ── Tab Change ────────────────────────────────────────────────── */
  const handleTabChange = (tab: LabelTab) => {
    setActiveTab(tab);
    setSearchText("");
    setSelectedRows([]);
    setCurrentPage(1);
  };

  /* ── Filter + Search Logic ─────────────────────────────────────── */

  const data: PrintRecord[] = apiData;

  const onSearch = (value: string | null) => {
    setSearchText(value ?? "");
    setCurrentPage(1);
  };

  const onAuditClick = () => {
    const payload = {
      modulename: tabLabelMap[activeTab],
      studyid: selectedStudy?.studyid
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const fileName = `${tabLabelMap[activeTab].replace(/\s+/g, "_")}_Audit_Log.pdf`;
          const pdfFile = new File([data], fileName, {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  /* ── Row Selection ─────────────────────────────────────────────── */

  const toggleRowSelect = (uniqueKey: string) => {
    setSelectedRows((prev) =>
      prev.includes(uniqueKey)
        ? prev.filter((id) => id !== uniqueKey)
        : [...prev, uniqueKey],
    );
  };



  /* ── Reprint Handlers ──────────────────────────────────────────── */

  const handleReprintClick = (row: PrintRecord) => {
    setReprintRecord(row);
    setReprintModalOpen(true);
  };

  const handleReprintConfirm = (form: any) => {
    setReprintModalOpen(false);
    setReprintRecord(null);
    setSelectedRows([]);
  };

  const handleReprintClose = () => {
    setReprintModalOpen(false);
    setReprintRecord(null);
  };

  const { hasPermission, isModuleVisible } = usePermission();

  const permissionMap: Record<LabelTab, string> = {
    subject: PERMISSION_MODULES.SUBJECT_LABEL,
    idcard: PERMISSION_MODULES.SUBJECT_ICARD_LABEL,
    vacutainer: PERMISSION_MODULES.VACUTAINER_LABEL,
    cryovial: PERMISSION_MODULES.RIAVIAL_LABEL,
    box: PERMISSION_MODULES.BOXLABEL,
    dispensing: PERMISSION_MODULES.DISPENSING_LABEL,
  };

  const permittedTabs = (["subject", "idcard", "vacutainer", "cryovial", "box", "dispensing"] as LabelTab[]).filter((key) => {
    const perm = permissionMap[key];
    return hasPermission(perm) && isModuleVisible(perm);
  });

  useEffect(() => {
    if (permittedTabs.length > 0 && !permittedTabs.includes(activeTab)) {
      setActiveTab(permittedTabs[0]);
    }
  }, [activeTab, permittedTabs]);

  const canprint = hasPermission(permissionMap[activeTab], PERMISSION_ACTIONS.PRINT);

  /* ── Columns ──────────────────────────────────────────────────── */

  const columns = reprintColumn(
    selectedRows,
    toggleRowSelect,
    handleReprintClick,
    canprint,
    activeTab
  );

  /* ── Render ────────────────────────────────────────────────────── */

  return (
    <div
      className={styles.dashboardContainer}
      style={{ fontFamily: '"hkgrotesk", "Poppins", sans-serif' }}
    >
      <Container fluid>
        {!selectedStudy?.studyid ? (
          <div >
            <NoStudySelectedPlaceholder moduleName="print history" />
          </div>
        ) : (
          <>
            {/* ── Page Header ──────────────────────────────────────── */}
            <div className="d-flex justify-content-between align-items-center mt-4 mb-4 px-2">
              <div className="d-flex align-items-center gap-3">
                <button
                  className="btn btn-link p-0 text-muted"
                  onClick={() => navigate(-1)}
                  style={{ fontSize: 18 }}
                ></button>
                <div>
                  <h5
                    style={{
                      fontSize: "1.375rem",
                      fontWeight: 700,
                      color: "#0f172a",
                      margin: 0,
                      lineHeight: 1.3,
                    }}
                  >
                    Print History &amp; Reprint
                  </h5>
                  <p
                    style={{
                      fontSize: "0.8125rem",
                      color: "#64748b",
                      margin: "2px 0 0",
                    }}
                  >
                    Review recent print jobs and reprint labels if necessary.
                  </p>
                </div>
              </div>

              <div className="d-flex gap-3 align-items-center">
                <CommonButton
                  module={permissionMap[activeTab]}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                  className="fw-600"
                  style={{ height: "35px" }}
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  module={permissionMap[activeTab]}
                  action={PERMISSION_ACTIONS.FETCH_PRINT_STATUS}
                  color="secondary"
                  outline
                  className="d-flex align-items-center px-3 fw-600"
                  disabled={isManualRefetch && SelectAllQuery.isFetching}
                  onClick={() => {
                    setIsManualRefetch(true);
                    SelectAllQuery.refetch();
                  }}
                >
                  <i
                    className={`ri-refresh-line fs-18 me-1 ${isManualRefetch && SelectAllQuery.isFetching
                      ? "spin-icon"
                      : ""
                      }`}
                  />
                  {isManualRefetch && SelectAllQuery.isFetching
                    ? "Refreshing..."
                    : "Fetch Print Status"}
                </CommonButton>
                <CommonButton
                  module={permissionMap[activeTab]}
                  action={PERMISSION_ACTIONS.PRINT}
                  color="primary"
                  outline
                  className="d-flex align-items-center px-3 fw-600"
                  disabled={selectedRows.length === 0}
                  onClick={() => {
                    const selected = data.filter((r) =>
                      selectedRows.includes(r.uniqueKey!),
                    );
                    if (selected.length > 0) {
                      setReprintRecord(selected[0]);
                      setReprintModalOpen(true);
                    }
                  }}
                >
                  <i className="ri-printer-line fs-18 me-1" /> Reprint Selected
                  {selectedRows.length > 0 ? ` (${selectedRows.length})` : ""}
                </CommonButton>
              </div>
            </div>

            {/* ── Table Card ───────────────────────────────────────── */}
            <div className={styles.listPanel}>
              {/* ── Panel Header: Search + Filter ────────────────── */}
              <div className={styles.panelHeader}>
                <h2 className="d-flex align-items-center">
                  <i className="ri-history-line me-2 text-primary fs-18" />
                  Print History
                </h2>

                <CommonSearch onSearch={onSearch} searchText={searchText} />
              </div>

              {/* ── Label Type Tabs ──────────────────────────────── */}
              <LabelTypeTabs activeTab={activeTab} onTabChange={handleTabChange} />

              {/* ── Common Table ──────────────────────────────────── */}
              <div className="px-4 pb-3">
                <CommonTableIndexWise
                  columns={columns}
                  data={data}
                  hideAction={true}
                  loading={SelectAllQuery.isLoading || SelectAllQuery.isFetching}
                  totalCount={totalRecords}
                  totalRecords={totalRecords}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
            </div>
          </>
        )}
      </Container>

      {/* ── Reprint Modal ── */}
      <SubjectPrintModel
        isOpen={reprintModalOpen}
        record={reprintRecord}
        onClose={handleReprintClose}
        onConfirm={handleReprintConfirm}
        selectedCount={selectedRows.length}
        selectedRecords={data.filter((r) => selectedRows.includes(r.uniqueKey!))}
      />

      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
    </div>
  );
};

export default RePrintLabelList;
 